import React from "react";
import Editor,{useMonaco} from "@monaco-editor/react";

/**
 * Lightweight Remedy qualification syntax check:
 * - balanced 'field' / "value" quotes and parentheses
 * - field references must exist on the form (when fields are known)
 * - no dangling AND/OR
 * ${...} placeholders are always allowed.
 */
// formFields entries may be plain strings (Remedy field names) or objects
// {name, description} from connector metadata (e.g. Innovation Studio:
// name = field id used in queries, description = human-readable field name)
export function normalizeField(f) {
    return typeof f === "string" ? { name: f } : (f || { name: "" });
}

// Every connector declares its own query language (see /v1/connectors):
// {name, fieldQuote, valueQuote, example}. fieldQuote "'" = AR-style
// qualification (single-quoted field refs, validated against known fields);
// '' = bare field names (JQL/EMS/SQL-ish) - only balance checks apply.
export const AR_LANGUAGE = { name: "AR Qualification", fieldQuote: "'", valueQuote: '"' };

// Completion providers are registered ONCE per language id ("SC_"+form),
// but fields/queryLanguage load asynchronously and can change - the provider
// therefore reads them live from this registry instead of its mount closure.
const LANG_META = new Map();

// resolve the declared query language of an entry's connector
export function queryLanguageFor(connectors, connector) {
    const c = (connectors || []).find((x) => x.name === (connector || "remedy"));
    return (c && c.queryLanguage) || AR_LANGUAGE;
}

export function validateRemedyQuery(text, formFields, monaco, queryLanguage) {
    const markers = [];
    if (!text || !text.trim()) {
        return markers;
    }
    // a connector may opt out entirely for exotic languages (JSON queries,
    // GraphQL, ...) where even the generic balance checks would be wrong
    if (queryLanguage && queryLanguage.validate === false) {
        return markers;
    }
    if (queryLanguage && queryLanguage.fieldQuote !== "'") {
        return validateGenericQuery(text, monaco);
    }

    const pos = (idx) => {
        const upTo = text.substring(0, idx);
        const lines = upTo.split("\n");
        return { line: lines.length, col: lines[lines.length - 1].length + 1 };
    };
    const addMarker = (severity, message, fromIdx, toIdx) => {
        const start = pos(fromIdx);
        const end = pos(toIdx);
        markers.push({
            severity: severity,
            message: message,
            startLineNumber: start.line,
            startColumn: start.col,
            endLineNumber: end.line,
            endColumn: end.col + 1
        });
    };

    const ERROR = monaco.MarkerSeverity.Error;
    const WARNING = monaco.MarkerSeverity.Warning;

    const knownFields = (formFields || []).map((f) => String(normalizeField(f).name).toLowerCase());
    let depth = 0;
    let i = 0;
    const parenStack = [];

    while (i < text.length) {
        const c = text[i];
        if (c === "'") {
            const close = text.indexOf("'", i + 1);
            if (close === -1) {
                addMarker(ERROR, "Unclosed field quote (')", i, text.length);
                break;
            }
            const fieldRef = text.substring(i + 1, close);
            if (
                knownFields.length > 0 &&
                fieldRef.indexOf("${") === -1 &&
                knownFields.indexOf(fieldRef.toLowerCase()) === -1
            ) {
                addMarker(ERROR, `Field '${fieldRef}' does not exist on form`, i, close + 1);
            }
            i = close + 1;
        } else if (c === '"') {
            const close = text.indexOf('"', i + 1);
            if (close === -1) {
                addMarker(ERROR, 'Unclosed value quote (")', i, text.length);
                break;
            }
            i = close + 1;
        } else if (c === "(") {
            depth++;
            parenStack.push(i);
            i++;
        } else if (c === ")") {
            depth--;
            if (depth < 0) {
                addMarker(ERROR, "Unmatched closing parenthesis", i, i + 1);
                depth = 0;
            } else {
                parenStack.pop();
            }
            i++;
        } else {
            i++;
        }
    }
    if (depth > 0 && parenStack.length > 0) {
        addMarker(ERROR, "Unclosed parenthesis", parenStack[parenStack.length - 1], parenStack[parenStack.length - 1] + 1);
    }

    const trailing = text.trim().match(/\b(AND|OR|LIKE|=|!=|<|>|<=|>=)$/i);
    if (trailing) {
        addMarker(WARNING, `Query ends with operator "${trailing[1]}"`, text.length - trailing[1].length, text.length);
    }

    return markers;
}


/**
 * JQL / EMS filters: field names are unquoted, both quote styles delimit
 * VALUES - so only balance checks and dangling operators apply, never
 * field-existence (that grammar belongs to the 'ar' dialect).
 */
function validateGenericQuery(text, monaco) {
    const markers = [];
    const pos = (idx) => {
        const upTo = text.substring(0, idx);
        const lines = upTo.split("\n");
        return { line: lines.length, col: lines[lines.length - 1].length + 1 };
    };
    const addMarker = (severity, message, fromIdx, toIdx) => {
        const start = pos(fromIdx);
        const end = pos(toIdx);
        markers.push({
            severity: severity,
            message: message,
            startLineNumber: start.line,
            startColumn: start.col,
            endLineNumber: end.line,
            endColumn: end.col + 1
        });
    };
    const ERROR = monaco.MarkerSeverity.Error;
    const WARNING = monaco.MarkerSeverity.Warning;

    let depth = 0;
    let i = 0;
    const parenStack = [];
    while (i < text.length) {
        const c = text[i];
        if (c === "'" || c === '"') {
            const close = text.indexOf(c, i + 1);
            if (close === -1) {
                addMarker(ERROR, `Unclosed value quote (${c})`, i, text.length);
                break;
            }
            i = close + 1;
        } else if (c === "(") {
            depth++;
            parenStack.push(i);
            i++;
        } else if (c === ")") {
            depth--;
            if (depth < 0) {
                addMarker(ERROR, "Unmatched closing parenthesis", i, i + 1);
                depth = 0;
            } else {
                parenStack.pop();
            }
            i++;
        } else {
            i++;
        }
    }
    if (depth > 0 && parenStack.length > 0) {
        addMarker(ERROR, "Unclosed parenthesis", parenStack[parenStack.length - 1], parenStack[parenStack.length - 1] + 1);
    }
    const trailing = text.trim().match(/\b(AND|OR|LIKE|IN|=|!=|~|<|>|<=|>=)$/i);
    if (trailing) {
        addMarker(WARNING, `Query ends with operator "${trailing[1]}"`, text.length - trailing[1].length, text.length);
    }
    return markers;
}

export default function RemedyEditor(props){

    const {formFields,config,form,index,minimap,queryLanguage}=props;
    const ql = queryLanguage || AR_LANGUAGE;
    LANG_META.set("SC_"+form, { fields: formFields, ql: ql });

    const fieldsRef = React.useRef(formFields);
    fieldsRef.current = formFields;
    const qlRef = React.useRef(ql);
    qlRef.current = ql;
    const editorRef = React.useRef(null);
    const monacoRef = React.useRef(null);

    const runValidation = React.useCallback(() => {
        if (editorRef.current && monacoRef.current) {
            const model = editorRef.current.getModel();
            if (model) {
                monacoRef.current.editor.setModelMarkers(
                    model,
                    "remedy-validate",
                    validateRemedyQuery(model.getValue(), fieldsRef.current, monacoRef.current, qlRef.current)
                );
            }
        }
    }, []);

    React.useEffect(() => {
        runValidation();
    }, [formFields, runValidation]);




    function handleEditorWillMount(monaco){

        if ( monaco && monaco.languages.getLanguages().filter(l=>l.id=="SC_"+form).length==0){



            monaco.languages.register({ id: "SC_"+form });
            monaco.languages.setMonarchTokensProvider("SC_"+form, {
                tokenizer: {
                    root: [
                        [/'.*?'/, "field-name"],
                        [/\".*?\"/, "field-value"],
                        [/\$.*?\$/, "keyword"],
                        [/\[info.*/, "custom-info"],
                        [/\[[a-zA-Z 0-9:]+\]/, "custom-date"],
                    ]
                }
            });


            monaco.languages.registerCompletionItemProvider("SC_"+form, {
                provideCompletionItems: (model,position) => {

                    const word = model.getWordUntilPosition(position);
                    const range = {
                        startLineNumber: position.lineNumber,
                        endLineNumber: position.lineNumber,
                        startColumn: word.startColumn-1,
                        endColumn: word.startColumn
                    };

                    const range2 = {
                        startLineNumber: position.lineNumber,
                        endLineNumber: position.lineNumber,
                        startColumn: word.endColumn+1,
                        endColumn: word.endColumn+2
                    };
                    const range3 = {
                        startLineNumber: position.lineNumber,
                        endLineNumber: position.lineNumber,
                        startColumn: word.startColumn-1,
                        endColumn: word.startColumn
                    };
                    const check=model.getValueInRange(range);
                    const check2=model.getValueInRange(range2);
                    const check3=model.getValueInRange(range3);

                    let sugFields=[];

                    // live lookup - the mount-time props may predate the
                    // async field metadata (e.g. freshly added jira entries)
                    const meta = LANG_META.get("SC_"+form) || {};
                    const liveFields = meta.fields || [];
                    const liveQl = meta.ql || AR_LANGUAGE;

                    if (check != '"' && check2 != '"'){
                        sugFields=(liveFields||[]).map(raw=>{
                            const f = normalizeField(raw);
                            // insert pattern: explicit template beats the
                            // quote-derived default ({field} = field name,
                            // $0 = cursor position, e.g. "{field} eq '$0'")
                            const fq = liveQl.fieldQuote || "";
                            const vq = liveQl.valueQuote || '"';
                            let insert;
                            if (liveQl.insertPattern) {
                                insert = liveQl.insertPattern.split("{field}").join(f.name);
                            } else if (fq === "'") {
                                insert = (check3=="'")?f.name+"'="+vq+"$0"+vq:"'"+f.name+"'="+vq+"$0"+vq;
                            } else {
                                insert = f.name + " = " + vq + "$0" + vq;
                            }
                            return {
                                label: f.description ? `${f.name} — ${f.description}` : f.name,
                                filterText: f.description ? `${f.name} ${f.description}` : f.name,
                                kind: monaco.languages.CompletionItemKind.Field,
                                insertText: insert,
                                insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                            };
                        });
                    }

                    const suggest=[
                        ...sugFields,
                        {
                            label: 'OR',
                            kind: monaco.languages.CompletionItemKind.Keyword,
                            insertText: ' OR ($0)',
                            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet
                        },
                        {
                            label: 'AND',
                            kind: monaco.languages.CompletionItemKind.Keyword,
                            insertText: ' AND ($0)',
                            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                        }];


                    return {suggestions: suggest};
                }
            });
            monaco.editor.defineTheme('myCoolTheme', {
                base: 'vs-dark',
                inherit: false,
                rules: [
                    { token: 'custom-info', foreground: '9C9284' },
                    { token: 'field-name', foreground: 'F58A8A', fontStyle: 'bold' },
                    { token: 'field-value', foreground: 'F5A25E' },
                    { token: 'keyword', foreground: '3BB584' },
                ],
                colors: {
                    'editor.foreground': '#F3EEE4',
                    'editor.background': '#282216',
                    'editorCursor.foreground': '#F0812E',
                    'editorLineNumber.foreground': '#7E7561',
                    'editor.lineHighlightBackground': '#2E2718',
                    'editor.selectionBackground': '#3A2915'
                }
            });


        }
    }

    function handleEditorDidMount(editor,monaco){

        monaco.editor.setTheme('myCoolTheme');
        editorRef.current = editor;
        monacoRef.current = monaco;
        editor.onDidChangeModelContent(runValidation);
        runValidation();
    }


    return (


        <Editor
            options={{minimap}}
            path={index}
            defaultLanguage={"SC_"+form}
            onMount={handleEditorDidMount}
            beforeMount={handleEditorWillMount}
            {...props}
        />)




}




